/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { ConcertinaService } from '../../services/component/concertina.service';
import { ConcertinaContentComponent } from './concertina-content.component';
import { ConcertinaItemComponent } from './concertina-item.component';
import { ConcertinaTitleComponent } from './concertina-title.component';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../../../../core/components';

/**
 * Reference to the component
 */
let counter = 0;

@Component({
    selector: 'app-concertina-container',
    templateUrl: './concertina-container.component.html',
    styleUrls: ['./concertina.component.scss'],
})

export class ConcertinaContainerComponent extends BaseComponent implements AfterViewInit {
    /**
     * Get the concertina title component object.
     */
    @ContentChildren(ConcertinaTitleComponent) public titles;

    /**
     * Get the concertina items component object.
     */
    @ContentChildren(ConcertinaItemComponent) public items;

    /**
     * Get the concertina content component object.
     */
    @ContentChildren(ConcertinaContentComponent) public contents;

    /**
     * Reference to the 1st panel in the html.
     */
    @ViewChild('panel1', { static: true }) public panel1: ElementRef;

    /**
     * Reference to the 2nd panel in the html.
     */
    @ViewChild('panel2', { static: true }) public panel2: ElementRef;

    /**
     * Boolean to set styling.
     */
    @Input() public fullWidth: boolean = false;

    /**
     * Boolean to set styling.
     */
    @Input() public dark: boolean = true;

    /**
     * Menu class for changing between different colours
     */
    @Input() public classList: 'menu level-1' | 'menu level-2' | 'menu level-3' | 'menu' | '' = '';

    /**
     * Add the vertical class
     */
    @Input() public vertical: boolean;

    /**
     * Emit changes
     */
    @Output() public eventEmitter = new EventEmitter();

    /**
     * Store the state of the concertina contents.
     */
    public activeIndex: number = -1;

    /**
     * Open state of the concertina.
     */
    public open: boolean = false;

    /**
     * Class reference to the component counter
     * @private
     */
    private readonly componentNumber: number = 0;

    /**
     * Component id made up of class counter and panel.
     * @private
     */
    private readonly componentId: string;

    constructor(
        private cdr: ChangeDetectorRef,
        private concertinaService: ConcertinaService
    ) {
        super();

        this.setupSubscribers();

        // Setup class identifier variables.
        this.componentNumber = counter++;
        this.componentId = 'panel' + this.componentNumber;
    }

    public ngAfterViewInit() {
        // Check if there are any active items onload.
        const items = this.items.toArray();
        for (const item of items) {
            if (item.active) {
                this.activeIndex = item['index'];
                this.concertinaService.addIndex(this.formatActiveItem(item));
            }
        }

        this.cdr.detectChanges();
    }

    /**
     * Emit the new selected tab.
     */
    public itemClicked(item): void {
        if (!item.enabled) {
            return;
        }

        this.activeIndex = item['index'];
        this.concertinaService.removeIndex(this.componentId);
        this.concertinaService.addIndex(this.formatActiveItem(item));
        this.concertinaService.selectionChanged.next(item);
        this.open = true;
    }

    /**
     * Toggle open state.
     */
    public toggleConcertina(): void {
        this.open = !this.open;
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.concertinaService.selectionChanged
            .pipe(takeUntil(this.destroyed$))
            .subscribe(componentId => {
                if (typeof componentId === 'string') {
                    const componentNumber = componentId.replace(/^\D+/g, '');
                    // If the component number is greater than the one clicked, close it!
                    if (parseInt(componentNumber, 10) < this.componentNumber) {
                        this.open = false;
                        this.activeIndex = -1;
                        this.concertinaService.removeIndex(this.componentId);
                    }
                }
            });
    }

    /**
     * Format active item.
     * @param item
     * @private
     */
    private formatActiveItem(item): any {
        item.active = true;
        item.componentId = this.componentId;

        return {
            componentId: this.componentId,
            index: item['index'],
            title: item.title
        };
    }
}
